import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Purser — Baker & Sage's payment rail. Phase 1 · in-house merchant only.
 *
 * The public-facing page. Founder ruling 2026-08-11:
 *  - No "PayPal-killer" language
 *  - No Musk-press hype
 *  - Announce the price war by publishing the comparison, not by saying it out loud
 *  - Rail hierarchy visible: crypto cheapest → ACH → card → PayPal
 *  - Cash-discount program (legal in 50 states, does not violate PayPal ToS)
 */
export default function PurserPage() {
  const [amount, setAmount] = useState(100);
  const [compare, setCompare] = useState(null);
  const [rails, setRails] = useState(null);

  useEffect(() => {
    const a = Math.max(1, Number(amount) || 100);
    fetch(`${API}/api/purser/compare?amount_usd=${a}`)
      .then((r) => r.json()).then(setCompare).catch(() => setCompare(null));
    fetch(`${API}/api/purser/rails?amount_usd=${a}`)
      .then((r) => r.json()).then(setRails).catch(() => setRails(null));
  }, [amount]);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="purser-page">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">
          Baker &amp; Sage's own payment rail · Phase 1 · in-house
        </div>
        <div className="flex items-start gap-4 mb-4">
          <div className="text-sage-accent shrink-0"><ProductMark slug="purser" size={52} /></div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase">
            Purser
          </h1>
        </div>
        <p className="text-lg md:text-xl text-sage-muted mb-8 leading-relaxed sage-prose max-w-3xl">
          The rail we use ourselves — and open to the family when we've earned the licenses.
          Pay how you want. Crypto is cheapest. Bank transfer next. Card and PayPal exist for anyone who still prefers them.
        </p>

        {/* Interactive price comparison — the announcement */}
        <div className="border-2 border-sage-accent bg-sage-pale/30 p-6 md:p-8 mb-12" data-testid="price-comparison">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-3">
            Price comparison · you set the amount
          </div>
          <div className="flex flex-wrap items-baseline gap-3 mb-6">
            <label className="text-sm text-sage-muted uppercase tracking-wider">Sticker price</label>
            <div className="flex items-center border border-sage-accent bg-sage-bg px-3 py-1">
              <span className="text-sage-accent mr-1">$</span>
              <input
                data-testid="compare-amount-input"
                type="number"
                min={1}
                step={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-24 bg-transparent outline-none text-sage-text text-lg font-bold"
              />
            </div>
          </div>

          {compare && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="text-sage-text">
                    <th className="text-left px-3 py-2 uppercase text-xs tracking-wider">Processor</th>
                    <th className="text-right px-3 py-2 uppercase text-xs tracking-wider">Customer pays</th>
                    <th className="text-right px-3 py-2 uppercase text-xs tracking-wider">Savings vs. Stripe</th>
                    <th className="text-right px-3 py-2 uppercase text-xs tracking-wider">Savings vs. PayPal</th>
                  </tr>
                </thead>
                <tbody className="text-sage-muted">
                  <tr className="border-t border-sage-accent/50 bg-sage-accent/10" data-testid="row-purser-crypto">
                    <td className="px-3 py-2"><strong className="text-sage-text">Purser · Crypto (USDC)</strong></td>
                    <td className="text-right px-3 py-2 text-sage-accent font-bold">${compare.prices.purser_crypto.total.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_crypto.savings_vs_stripe.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_crypto.savings_vs_paypal.toFixed(2)}</td>
                  </tr>
                  <tr className="border-t border-sage-border" data-testid="row-purser-ach">
                    <td className="px-3 py-2"><strong className="text-sage-text">Purser · Bank transfer (ACH)</strong></td>
                    <td className="text-right px-3 py-2 text-sage-accent font-bold">${compare.prices.purser_ach.total.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_ach.savings_vs_stripe.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_ach.savings_vs_paypal.toFixed(2)}</td>
                  </tr>
                  <tr className="border-t border-sage-border" data-testid="row-purser-card">
                    <td className="px-3 py-2"><strong className="text-sage-text">Purser · Card</strong></td>
                    <td className="text-right px-3 py-2 text-sage-text font-bold">${compare.prices.purser_card.total.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_card.savings_vs_stripe.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-accent">+${compare.prices.purser_card.savings_vs_paypal.toFixed(2)}</td>
                  </tr>
                  <tr className="border-t border-sage-border" data-testid="row-stripe">
                    <td className="px-3 py-2 italic">Stripe</td>
                    <td className="text-right px-3 py-2 italic">${compare.prices.stripe.total.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-muted italic">—</td>
                    <td className="text-right px-3 py-2 text-sage-muted italic">—</td>
                  </tr>
                  <tr className="border-t border-sage-border" data-testid="row-paypal">
                    <td className="px-3 py-2 italic">PayPal</td>
                    <td className="text-right px-3 py-2 italic">${compare.prices.paypal.total.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-sage-muted italic">—</td>
                    <td className="text-right px-3 py-2 text-sage-muted italic">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-sage-muted mt-3 italic sage-prose">
            Stripe: 2.9% + $0.30 per transaction. PayPal: 2.9% + $0.49 per transaction. Purser's number is what the customer pays after the cash-discount for choosing a cheaper rail.
          </p>
        </div>

        {/* Rail hierarchy */}
        <div className="mb-12" data-testid="rail-hierarchy">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Pay how you want</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6">The four rails, cheapest first</h2>
          {rails && (
            <div className="space-y-3">
              {rails.rails.map((r, i) => (
                <RailRow key={r.rail} idx={i} rail={r} />
              ))}
            </div>
          )}
        </div>

        {/* Phased roadmap · earned honestly */}
        <div className="mb-12" data-testid="roadmap">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The road ahead</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6">Phased rollout · earned honestly</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PhaseCard n={1} title="In-house merchant" state="live" body="Baker &amp; Sage collects payments for its own products. Cash-discount rails visible. No money-transmitter licensing required at this stage." />
            <PhaseCard n={2} title="Sibling houses" state="next" body="Once Baker &amp; Sage revenue supports it, Purser opens to Aethelgard, Resilience, Soljins — still under our own MSB + one state MTL." />
            <PhaseCard n={3} title="External merchants" state="future" body="High-value state MTLs filed (CA, NY, TX, FL, IL). Purser opens to strangers under the same rail pricing. All licenses paid, nothing shortcutted." />
            <PhaseCard n={4} title="Full national rail" state="future" body="50-state coverage. Every acquirer partnership signed. Reserves fully funded from prior-phase revenue." />
          </div>
        </div>

        {/* Six-gate hardening */}
        <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="moats">
          <MoatCard title="Cross-rail redundancy" body="Four independent rails. If one denies, the others carry. Redundancy is built inside Purser, not bolted on outside." />
          <MoatCard title="Honesty ledger" body="Every dollar Purser touches lands on a public tally. Every refund door is one click. Every dispute is filed with the customer named on the receipt." />
          <MoatCard title="Six-gate hardening" body="Webhook signature verify · capture idempotency · master-only refund · CORS allow-list · rate-limit middleware · secrets discipline. All six sealed before the door opens." />
        </div>

        {/* Legal note · quiet and honest */}
        <div className="border-l-4 border-sage-muted pl-6 mb-12" data-testid="legal-note">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-1">Legal note</div>
          <div className="text-sm text-sage-muted sage-prose">
            Cash-discount programs — advertising a baseline price and offering a discount for cheaper rails — are permitted in all 50 US states.
            Purser does not surcharge PayPal transactions; that would violate PayPal's terms of service.
            The cheaper rails are cheaper because their real underlying cost is lower, and the savings pass through to the customer directly.
          </div>
        </div>
      </div>
    </div>
  );
}

function RailRow({ rail, idx }) {
  const state = rail.provider_armed ? "armed" : "not-armed";
  return (
    <div
      data-testid={`rail-${rail.rail}`}
      className={`border ${rail.recommended ? "border-sage-accent" : "border-sage-border"} p-4 flex items-center justify-between gap-4 ${rail.recommended ? "bg-sage-pale/20" : ""}`}
    >
      <div className="flex items-baseline gap-4 min-w-0">
        <div className="text-xl font-bold text-sage-muted w-6">{idx + 1}</div>
        <div className="min-w-0">
          <div className="text-lg font-bold uppercase tracking-tight text-sage-text">{rail.label}</div>
          <div className="text-xs text-sage-muted mt-1">{rail.provider} · {rail.settlement}</div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-lg font-bold text-sage-text">${rail.customer_pays.toFixed(2)}</div>
        {rail.discount_dollars > 0 ? (
          <div className="text-xs text-sage-accent">save ${rail.discount_dollars.toFixed(2)}</div>
        ) : (
          <div className="text-xs text-sage-muted">baseline</div>
        )}
        <div className={`text-[10px] uppercase tracking-wider mt-1 ${state === "armed" ? "text-sage-accent" : "text-sage-muted"}`}>
          {state === "armed" ? "LIVE" : "PHASE 2"}
        </div>
      </div>
    </div>
  );
}

function PhaseCard({ n, title, state, body }) {
  const label = { live: "LIVE", next: "NEXT", future: "PLANNED" }[state];
  const color = { live: "sage-accent", next: "sage-warn", future: "sage-muted" }[state];
  return (
    <div className="border border-sage-border p-4" data-testid={`phase-${n}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="text-3xl font-bold text-sage-muted">{n}</div>
        <div className={`text-[10px] uppercase tracking-[0.15em] border border-${color} text-${color} px-2 py-0.5`}>{label}</div>
      </div>
      <div className="text-base font-bold uppercase text-sage-text mb-2">{title}</div>
      <div className="text-xs text-sage-muted sage-prose">{body}</div>
    </div>
  );
}

function MoatCard({ title, body }) {
  return (
    <div className="border border-sage-border p-5" data-testid={`moat-${title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <div className="text-lg font-bold uppercase tracking-tight text-sage-text mb-2">{title}</div>
      <div className="text-sm text-sage-muted sage-prose">{body}</div>
    </div>
  );
}
